import fallbackCss from './documentBase.css?raw';

// Class applied to every document surface — the editor content area
// (`EditorContent`), the canonical preview (`RichTextPreview`), and the
// backend-rendered PDF/mail body. All `.cd-doc` rules come from
// `documentBase.css` (served live from the backend, bundled here as fallback).
export const CD_DOC_CLASS = 'cd-doc';

// The bundled fallback bytes (kept in sync with the backend provider).
export const documentBaseFallbackCss = fallbackCss;

const STYLE_ID = 'cd-document-base';
const CACHE_KEY = 'cd-document-base-v1';

interface CachedCss {
  etag: string | null;
  css: string;
}

export const readDocumentBaseCache = (): CachedCss | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CachedCss) : null;
  } catch {
    return null;
  }
};

export const writeDocumentBaseCache = (entry: CachedCss): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage unavailable / over quota — fine, we just lose the warm start.
  }
};

// Inject (or update in place) the single `<style id="cd-document-base">` in
// `<head>`. It is UNLAYERED on purpose: an unlayered `<style>` outranks every
// cascade layer, so it beats Tailwind preflight (`@layer base`) and the
// typography plugin — while still losing to inline `style=` (no `!important`),
// which is exactly the contract (tenant inline wins, base only fills structure).
export const injectDocumentBaseCss = (css: string): void => {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) {
    el.textContent = css;
  }
};

// First-paint guarantee: the moment this module is imported (it is pulled in by
// both the richText facades and the loader hook) inject the warm-cached copy,
// or the bundled fallback. The live copy from the endpoint replaces it once
// `useDocumentBaseStyles` resolves.
if (typeof document !== 'undefined') {
  injectDocumentBaseCss(readDocumentBaseCache()?.css ?? fallbackCss);
}
